// CNPJ alfanumérico (vigência jul/2026, Receita Federal / Serpro).
//
// Regras oficiais:
// - O CNPJ continua com 14 caracteres.
// - As 12 PRIMEIRAS posições (raiz 8 + ordem 4) aceitam letras maiúsculas A-Z
//   e dígitos 0-9. As 2 ÚLTIMAS (dígitos verificadores / DV) continuam SEMPRE
//   numéricas (0-9).
// - O cálculo do DV usa módulo 11 com valor(caractere) = código ASCII - 48
//   ('0'..'9' -> 0..9; 'A'..'Z' -> 17..42). Os pesos são os mesmos do CNPJ
//   numérico, então CNPJ numérico legado continua válido por este mesmo caminho.
// - A máscara visual NÃO muda: XX.XXX.XXX/XXXX-XX.
//
// Implementação de referência validada contra o exemplo oficial:
//   'AB12CD34EFGH' -> DV '83' -> 'AB.12C.D34/EFGH-83'
//   '54.550.752/0001-55' (numérico legado) -> válido.

const TAMANHO_CNPJ = 14;
const TAMANHO_BASE = 12; // raiz + ordem (sem os 2 DV)

// CNPJs com todos os caracteres iguais são inválidos (ex.: 00000000000000).
const CNPJS_INVALIDOS = [
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999'
];

// Normaliza: uppercase + remove tudo que não for [A-Z0-9] (mantém as letras!).
export const normalizeCnpj = (cnpj) =>
  (cnpj ?? '')
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

// Valor numérico de cada caractere para o módulo 11: ASCII - 48.
// '0'..'9' -> 0..9 ; 'A'..'Z' (ASCII 65..90) -> 17..42 (NÃO é A=10).
const charValue = (ch) => ch.charCodeAt(0) - 48;

// Calcula um dígito verificador percorrendo os valores em ordem reversa,
// multiplicando por um índice cíclico de 2 a 9 que reinicia em 2.
const verifierDigit = (values) => {
  let index = 2;
  let sum = 0;
  for (let i = values.length - 1; i >= 0; i--) {
    sum += values[i] * index;
    index = index === 9 ? 2 : index + 1;
  }
  const mod = sum % 11;
  return mod < 2 ? 0 : 11 - mod;
};

// Calcula os 2 DV a partir das 12 primeiras posições (já normalizadas).
// Retorna string de 2 dígitos, ex.: '83'.
export const calcCheckDigits = (base) => {
  const first12 = normalizeCnpj(base).slice(0, TAMANHO_BASE);
  if (!/^[A-Z0-9]{12}$/.test(first12)) {
    throw new Error('As 12 primeiras posições do CNPJ devem ter [A-Z0-9]');
  }
  const values = first12.split('').map(charValue); // pesos 5,4,3,2,9,8,7,6,5,4,3,2
  const dv1 = verifierDigit(values);
  values.push(dv1); // pesos 6,5,4,3,2,9,8,7,6,5,4,3,2
  const dv2 = verifierDigit(values);
  return `${dv1}${dv2}`;
};

// Valida formato + DV. Aceita alfanumérico e numérico legado, com ou sem máscara.
export const isValidCnpj = (cnpj) => {
  const stripped = normalizeCnpj(cnpj);
  if (stripped.length !== TAMANHO_CNPJ) return false;
  // 12 primeiras posições alfanuméricas, 2 últimas (DV) SEMPRE numéricas.
  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(stripped)) return false;
  if (CNPJS_INVALIDOS.includes(stripped)) return false;
  return calcCheckDigits(stripped) === stripped.slice(TAMANHO_BASE);
};

// Aplica a máscara visual XX.XXX.XXX/XXXX-XX a um CNPJ completo (14 chars).
// Para valores incompletos retorna o que conseguir mascarar por posição.
export const formatCnpj = (cnpj) => {
  const stripped = normalizeCnpj(cnpj);
  let out = stripped.slice(0, 2);
  if (stripped.length > 2) out += '.' + stripped.slice(2, 5);
  if (stripped.length > 5) out += '.' + stripped.slice(5, 8);
  if (stripped.length > 8) out += '/' + stripped.slice(8, 12);
  if (stripped.length > 12) out += '-' + stripped.slice(12, 14);
  return out;
};

// true quando o valor (limpo) tem o comprimento de um CNPJ (14), independente de validade.
export const hasCnpjLength = (value) => normalizeCnpj(value).length === TAMANHO_CNPJ;

const cnpj = {
  normalizeCnpj,
  calcCheckDigits,
  isValidCnpj,
  formatCnpj,
  hasCnpjLength
};

export default cnpj;
